import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FullResultCard } from "../components/ResultCardPreview";

// Standalone print page — no sidebar, no nav.
// Opens via window.open("/print/student/...") or window.open("/print/class/...")
// Fetches result data + resolved template from the API (using stored auth token),
// renders FullResultCard, then triggers window.print().

const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 0;
  }
  @media print {
    /* Force background colors and images to print (colored headers/tables) */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body { margin: 0 !important; padding: 0 !important; }
    .print-controls { display: none !important; }
    .print-area { display: block !important; padding: 0 !important; margin: 0 !important; }
    .print-card {
      box-shadow: none !important;
      page-break-after: always;
      break-after: page;
      page-break-inside: avoid;
    }
    .print-card:last-child { page-break-after: avoid; break-after: avoid; }
  }
  @media screen {
    body { background: #e5e7eb; min-height: 100vh; font-family: sans-serif; }
    .print-controls {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      background: #1E3A5F; color: #fff;
      display: flex; align-items: center; gap: 16px;
      padding: 10px 24px; box-shadow: 0 2px 8px rgba(0,0,0,.3);
    }
    .print-controls h2 { margin: 0; font-size: 16px; flex: 1; }
    .print-btn {
      background: #F59E0B; color: #000; border: none; cursor: pointer;
      font-weight: 700; font-size: 14px; padding: 8px 20px; border-radius: 8px;
    }
    .print-btn:hover { background: #FBBF24; }
    .close-btn {
      background: rgba(255,255,255,.15); color: #fff; border: none; cursor: pointer;
      font-size: 14px; padding: 8px 16px; border-radius: 8px;
    }
    .print-area { padding: 80px 0 40px; display: flex; flex-direction: column; align-items: center; gap: 24px; }
    .print-card { background: #fff; box-shadow: 0 4px 24px rgba(0,0,0,.12); width: 794px; }
  }
`;

function buildApiUrl(mode, studentId, classSectionId, examId, sortBy) {
  if (mode === "class") return `/api/school-admin/print-data/class/${classSectionId}/exam/${examId}?sortBy=${sortBy || "roll_no"}`;
  return `/api/school-admin/print-data/student/${studentId}/exam/${examId}`;
}

export default function PrintResultCard() {
  const { studentId, classSectionId, examId } = useParams();
  const mode = classSectionId ? "class" : "student";

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // The global styles.css hides all body > *:not(.print-target) when printing.
  // Add print-target to #root so this page's content is visible in print mode.
  useEffect(() => {
    const root = document.getElementById("root");
    if (root) root.classList.add("print-target");
    return () => { if (root) root.classList.remove("print-target"); };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("vrm_token");
    const sortBy = new URLSearchParams(window.location.search).get("sortBy") || "roll_no";
    const url = buildApiUrl(mode, studentId, classSectionId, examId, sortBy);
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error(`Server returned ${r.status}`);
        return r.json();
      })
      .then((d) => setData(d))
      .catch((e) => setError(e.message));
  }, [mode, studentId, classSectionId, examId]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "sans-serif" }}>
        <h2 style={{ color: "#DC2626" }}>Failed to load result card</h2>
        <p style={{ color: "#555" }}>{error}</p>
        <button onClick={() => window.close()} style={{ marginTop: 16, padding: "8px 20px", cursor: "pointer" }}>Close</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "sans-serif" }}>
        <p style={{ color: "#555", fontSize: 18 }}>Loading result card…</p>
      </div>
    );
  }

  const { template } = data;
  const gradeScale = data.gradeScale || null;

  // Build the list of cards to render
  const cards = mode === "class"
    ? data.students.map(({ student, result, remark, attendance }) => ({ student, result, remark, attendance }))
    : [{ student: data.student, result: data.result, remark: data.remark, attendance: data.attendance }];

  const school = data.school;
  const exam = data.exam;

  const title = mode === "class"
    ? `Bulk Print — ${exam?.name} (${cards.length} students)`
    : `${data.student?.name} — ${exam?.name}`;

  return (
    <>
      <style>{PRINT_STYLES}</style>

      <div className="print-controls">
        <h2>🖨 {title}</h2>
        <button className="print-btn" onClick={() => window.print()}>Print / Save PDF</button>
        <button className="close-btn" onClick={() => window.close()}>✕ Close</button>
      </div>

      <div className="print-area">
        {cards.map(({ student, result, remark, attendance }, idx) => (
          <div className="print-card" key={student.id || idx}>
            <FullResultCard tpl={template} cols={1} printData={{ school, student, exam, result, remark, attendance, gradeScale }} />
          </div>
        ))}
      </div>
    </>
  );
}
